import AppCard from '@nxsys/components/AppCard';
import AppIconCard from '@nxsys/components/AppIconCard';
import { useGetStatisticsEmployeeIreland } from '@nxsys/hooks/api/dashboard';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useCanAccessEmployees } from '@nxsys/hooks/useCanAccessEmployees';
import { Icons } from '@nxsys/types/icons';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useEffect, useMemo, useState } from 'react';
import NewStarterModal from './components/NewStarterModal';
import AssignmentModal, {
  AssignmentModalTypeEnum,
} from './components/AssignmentModal';
import RPNUpdateModal from './components/RPNUpdateModal';
import AEPNUpdateModal from './components/AEPNUpdateModal';

const EmployeeIreland = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { value, loading, run } = useGetStatisticsEmployeeIreland();
  const data = useMemo(() => value?.data, [value]);
  // The cards drill into employee/subcontractor records, so a dashboard-only
  // user (no COMPANY.EMPLOYEES.LIST) sees the counts but cannot open them.
  const canAccessEmployees = useCanAccessEmployees();
  const [isOpenNewStarterModal, setIsOpenNewStarterModal] = useState(false);
  const [isOpenPlacementsModal, setIsOpenPlacementsModal] = useState(false);
  const [isOpenRPNUpdateModal, setIsOpenRPNUpdateModal] = useState(false);
  const [isOpenAEPNUpdateModal, setIsOpenAEPNUpdateModal] = useState(false);

  useEffect(() => {
    run();
  }, []);

  const items = [
    {
      title: data?.totalAllEmployees,
      description: 'All Employees',
      icon: <Icons.IconUserGroup />,
      onClick: () => navigate('/employees-subcontractors'),
    },
    {
      title: data?.totalNewStarter,
      description: 'New Starter',
      icon: <Icons.IconUserPlus />,
      onClick: () => setIsOpenNewStarterModal(true),
    },
    {
      title: data?.totalPlacements,
      description: 'Placements',
      icon: <Icons.IconFileText />,
      onClick: () => setIsOpenPlacementsModal(true),
    },
    {
      title: data?.totalRPN,
      helperTitleText:
        data?.totalRPNError && data.totalRPNError > 0
          ? `${data?.totalRPNError} errors`
          : '',
      description: 'RPN Updates',
      icon: <Icons.IconFileText />,
      onClick: () => setIsOpenRPNUpdateModal(true),
    },
    {
      title: data?.totalAEPN,
      helperTitleText:
        data?.totalAEPNError && data.totalAEPNError > 0
          ? `${data?.totalAEPNError} errors`
          : '',
      description: 'AEPN Updates',
      icon: <Icons.IconFileText />,
      onClick: () => setIsOpenAEPNUpdateModal(true),
    },
  ];

  return (
    <div className="EmployeeIreland">
      <AppCard className="p-[24px]" loading={loading}>
        <div className="flex justify-between items-center mb-[24px]">
          <AppIconCard iconName="IconDocs" title={messages['common.employees']} />
        </div>
        <div className="grid grid-cols-5 gap-[16px] max-sm:grid-cols-1">
          {items.map((item) => (
            <div
              key={item.title}
              className={`border border-[#CDDDD9] ${
                canAccessEmployees ? 'dashboard-hover-card' : ''
              } rounded-[8px] p-[16px]`}
              onClick={canAccessEmployees ? item.onClick : undefined}
            >
              <div className="flex items-center justify-between gap-x-[8px] mb-[34px]">
                <div className="bg-cyan-100 rounded-[8px] p-[8px]">
                  {item.icon}
                </div>
                {canAccessEmployees && <Icons.IconArrowRight />}
              </div>
              <div className="flex items-end gap-x-[8px] mb-[8px]">
                <div className="text-[22px] font-bold leading-none">
                  {item.title}
                </div>
                {item.helperTitleText && (
                  <span className="text-[14px] font-bold text-error-300">
                    {item.helperTitleText}
                  </span>
                )}
              </div>
              <div className="text-[14px] font-bold">{item.description}</div>
            </div>
          ))}
        </div>
      </AppCard>
      <NewStarterModal
        isOpen={isOpenNewStarterModal}
        toggle={() => setIsOpenNewStarterModal(false)}
      />
      <AssignmentModal
        isOpen={isOpenPlacementsModal}
        toggle={() => setIsOpenPlacementsModal(false)}
        type={AssignmentModalTypeEnum.PLACEMENTS}
      />
      <RPNUpdateModal
        isOpen={isOpenRPNUpdateModal}
        toggle={() => setIsOpenRPNUpdateModal(false)}
        cbAfterAction={() => run()}
      />
      <AEPNUpdateModal
        isOpen={isOpenAEPNUpdateModal}
        toggle={() => setIsOpenAEPNUpdateModal(false)}
        cbAfterAction={() => run()}
      />
    </div>
  );
};

export default EmployeeIreland;
